import { useEffect, useRef, useState } from 'react';
import { endpoints } from '../../api';
/** Восстановление читает только собственный ready UUID. Оно не повторяет upload и не создаёт заказ. */
export function useVideoSourcePreview(id: string | undefined, owner: number | undefined, kind: 'photo' | 'video', maximum: number) {
  const [local, setLocal] = useState<{ url: string; owner?: number } | null>(null),
    [attempt, retry] = useState(0);
  const [remote, setRemote] = useState<{ identity: string; url: string; error: boolean }>({ identity: '', url: '', error: false });
  const localUrl = useRef<string | null>(null),
    request = useRef<AbortController | null>(null);
  const identity = id && owner ? `${owner}:${id}:${kind}:${attempt}` : '';
  useEffect(
    () => () => {
      request.current?.abort();
      if (localUrl.current) URL.revokeObjectURL(localUrl.current);
    },
    [],
  );
  useEffect(() => {
    if (!identity || !id || !owner || (local && local.owner === owner)) return;
    const controller = new AbortController();
    request.current = controller;
    let disposed = false,
      url: string | null = null;
    const cancelled = new Promise<never>((_, reject) =>
      controller.signal.addEventListener('abort', () => reject(new DOMException('preview_cancelled', 'AbortError')), { once: true }),
    );
    const timer = setTimeout(() => controller.abort(), 30000);
    void (async () => {
      const me = await Promise.race([endpoints.me(), cancelled]);
      if (me.user.id !== owner) throw new Error('owner_changed');
      const blob = await Promise.race([endpoints.uploadPreview(id, controller.signal), cancelled]);
      if (disposed || controller.signal.aborted) return;
      const allowed = kind === 'video' ? ['video/mp4', 'video/quicktime'] : ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowed.includes(blob.type) || blob.size <= 0 || blob.size > maximum) throw new Error('preview_invalid');
      url = URL.createObjectURL(blob);
      setRemote({ identity, url, error: false });
    })()
      .catch(() => {
        if (!disposed) setRemote({ identity, url: '', error: true });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      disposed = true;
      controller.abort();
      clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
      if (request.current === controller) request.current = null;
    };
  }, [identity, id, owner, kind, maximum, local]);
  const select = (file: File | null) => {
    request.current?.abort();
    if (localUrl.current) URL.revokeObjectURL(localUrl.current);
    localUrl.current = file ? URL.createObjectURL(file) : null;
    setLocal(localUrl.current ? { url: localUrl.current, owner } : null);
  };
  const localVisible = local?.owner === owner ? local : null;
  return {
    url: localVisible?.url ?? (remote.identity === identity ? remote.url : ''),
    loading: !!identity && !localVisible && remote.identity !== identity,
    error: !!identity && !localVisible && remote.identity === identity && remote.error,
    select,
    retry: () => retry(v => v + 1),
  };
}
